import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollParallax";
import { supabase } from "@/integrations/supabase/client";
import { Globe, MapPin, Users, Star } from "lucide-react";
import counterTrips from "@/assets/counter-trips.jpg";
import counterCountries from "@/assets/counter-countries.jpg";
import counterExplorers from "@/assets/counter-explorers.jpg";
import counterReviews from "@/assets/counter-reviews.jpg";

function useCountUp(target: number, isVisible: boolean, duration = 2200) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!isVisible || target === 0) return;

    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, isVisible, duration]);

  return value;
}

const AnimatedCounters = () => {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollReveal({ threshold: 0.3 });
  const [stats, setStats] = useState({ trips: 0, countries: 0, users: 0, reviews: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      const [tripsRes] = await Promise.all([
        supabase.from("trips").select("countries", { count: "exact" }),
      ]);

      const trips = tripsRes.count || 0;
      const allCountries = new Set<string>();
      tripsRes.data?.forEach((trip) => {
        (trip.countries as string[])?.forEach((c) => allCountries.add(c));
      });

      setStats({ trips, countries: allCountries.size, users: 1804, reviews: 202 });
    };
    fetchStats();
  }, []);

  const tripCount = useCountUp(stats.trips, isVisible);
  const countryCount = useCountUp(stats.countries, isVisible);
  const userCount = useCountUp(stats.users, isVisible, 2600);
  const reviewCount = useCountUp(stats.reviews, isVisible, 2000);

  const counters = [
    { icon: Globe, value: tripCount, label: t("home.stat.trips", "Voyages créés"), suffix: "+", color: "hsl(200, 85%, 45%)", image: counterTrips },
    { icon: MapPin, value: countryCount, label: t("home.stat.countries", "Pays couverts"), suffix: "+", color: "hsl(24, 95%, 53%)", image: counterCountries },
    { icon: Users, value: userCount, label: t("home.stat.users", "Explorateurs"), suffix: "+", color: "hsl(160, 65%, 45%)", image: counterExplorers },
    { icon: Star, value: reviewCount, label: t("home.stat.reviews", "Avis déposés"), suffix: "+", color: "hsl(45, 90%, 50%)", image: counterReviews },
  ];

  return (
    <div ref={ref} className="relative py-10 overflow-hidden bg-background">

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {counters.map((counter, i) => (
            <div
              key={i}
              className="group relative rounded-2xl overflow-hidden border border-white/[0.06] hover:border-white/[0.15] transition-all duration-500"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
                transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 120}ms`,
              }}
            >
              {/* Background image */}
              <div className="absolute inset-0">
                <img
                  src={counter.image}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                  width={640}
                  height={512}
                />
                {/* Dark overlay for readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30 group-hover:from-black/70 group-hover:via-black/40 transition-all duration-500" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-5 md:p-7 flex flex-col items-center text-center min-h-[160px] md:min-h-[190px] justify-end">

                {/* Number */}
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1.5 tabular-nums tracking-tight drop-shadow-lg">
                  {counter.value.toLocaleString("fr-FR")}
                  <span className="text-lg md:text-xl ml-0.5 opacity-70">{counter.suffix}</span>
                </div>

                {/* Label */}
                <div className="text-sm md:text-base text-white/70 font-medium drop-shadow">{counter.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnimatedCounters;
